// utils/telefonoE164.ts — el teléfono de un usuario se guarda SIEMPRE con código de país (round 2026-10-30, decisión del dueño:
// «que sea obligatorio el código, y que sea parte del formato exigido para guardar teléfonos, así formateamos bien desde el comienzo»).
//
// FORMA CANÓNICA (E.164): «+» + código de país + número, SOLO dígitos después del «+», de 8 a 15 dígitos en total.
// Ejemplo: +56912345678. Sin espacios, guiones, paréntesis ni letras. «Cualquier país vale» (no se limita a +56).
//
// LA REGLA (la misma que aplicará el servidor; los casos viven en `telefonoE164.casos.json`, compartido con ese equipo):
//   1. Un teléfono VACÍO (null, "", solo espacios) es válido: el campo es opcional salvo que el formulario lo exija.
//   2. Lo que no es texto (número, objeto, arreglo, booleano) es inválido. Nunca se convierte.
//   3. Se normaliza con NFKC (los dígitos y el «+» de ancho completo pasan a ASCII, y casi todos los espacios Unicode pasan a un
//      espacio común). Luego se QUITAN los caracteres invisibles de formato (categoría Unicode Cf: marcas de dirección U+200E,
//      U+200F, U+202A a U+202E, espacio de ancho cero U+200B, unión de palabras U+2060, U+FEFF, el guion blando U+00AD…): un
//      número copiado de WhatsApp o de Contactos los trae pegados y la persona no los ve. Después se quitan los SEPARADORES:
//      cualquier espacio (el `\s` de JavaScript), el guion «-», los guiones largos (U+2010 a U+2015: «‐ ‑ ‒ – — ―»), el signo
//      menos U+2212, el punto y los paréntesis. Lo que quede debe ser `+?[0-9]+`: letras, otros símbolos, un «+» que no está al
//      inicio o dígitos que no son ASCII (árabe-índicos, persas…) son inválidos; NUNCA se descartan en silencio. Si tras quitar
//      los invisibles no queda nada (solo blancos), el valor es vacío.
//   4. Si empieza con «+» o con «00», lo que sigue ya trae el código de país: se toma el código conocido MÁS LARGO que calce
//      (un código desconocido es inválido). Si no, se antepone el código del país elegido en el selector; sin selector, falta el
//      código (así valida el servidor, que no tiene selector). El código del selector debe ser uno CONOCIDO exacto: «99» o «3»
//      (el comienzo de uno real) o «568» (uno real más un dígito) son CODIGO_PAIS_DESCONOCIDO, nunca se reinterpretan.
//   5. Para +56 se quita UN cero inicial del número nacional (la persona escribió 09…); si todavía queda otro cero, se rechaza
//      (CERO_INICIAL_REPETIDO): así el canónico nunca empieza con 0 tras el +56. Ningún otro país se altera.
//   6. El total (código + número) debe tener de 8 a 15 dígitos.
//   7. CÓDIGO REPETIDO (ronda F2): una persona con Chile elegido escribe «56 9 8765 4321» (así se escriben los chilenos en
//      WhatsApp) sin el «+». Se compondría +5656987654321, un número que no existe y que se guardaría en silencio. Regla, ya con
//      el código C y el número nacional N separados (y el 0 de +56 quitado): si N EMPIEZA con C y tiene MÁS dígitos que el
//      máximo plausible de un número nacional de ese país (`LARGO_NACIONAL_MAXIMO`, o `LARGO_NACIONAL_MAXIMO_GENERAL` si el país
//      no está en la tabla), el código está repetido y se rechaza con POSIBLE_CODIGO_REPETIDO. No se corrige solo: adivinar
//      cuál de los dos es el verdadero sería inventar un dato. Vale con y sin «+», para que el validador del servidor (que no
//      tiene selector) llegue al mismo resultado con el valor que emite el formulario. Se evalúa DESPUÉS del largo total
//      (MUY_CORTO / MUY_LARGO), así dos números pegados en un campo siguen diciendo «muy largo».
//   8. CHILE NUNCA EMPIEZA CON 56 (ronda F3, versión 3 del archivo de casos): un número nacional chileno jamás empieza con 56 (los
//      códigos de área son 2, 32 a 35, 41 a 45, 51 a 53, 55, 57, 58, 61, 63 a 65, 67, 71 a 73, 75 y los móviles 9). Con el código 56,
//      todo nacional que empiece con 56 es POSIBLE_CODIGO_REPETIDO SIN mirar el largo: la regla 7 sola dejaba pasar «56 9876 543» y
//      «569876543» (9 dígitos, justo el máximo) como +56569876543, un número que no existe, y la vista previa lo mostraba como si
//      fuera válido. Vale con y sin «+», igual que la regla 7.
//
// Este archivo NO depende de React ni de Ant Design: es lógica pura, para poder probarla con los casos compartidos.
import { comoTextoComparable } from "./camposCambiados";

/** Códigos de país conocidos (ITU E.164 geográficos). Es una lista SIN prefijos repetidos: ningún código es el comienzo de otro. */
export const CODIGOS_PAIS_CONOCIDOS: readonly string[] = [
  // Zona 1 y 7
  "1", "7",
  // Zona 2 (África y otros)
  "20", "211", "212", "213", "216", "218", "220", "221", "222", "223", "224", "225", "226", "227", "228", "229",
  "230", "231", "232", "233", "234", "235", "236", "237", "238", "239", "240", "241", "242", "243", "244", "245",
  "246", "247", "248", "249", "250", "251", "252", "253", "254", "255", "256", "257", "258", "260", "261", "262",
  "263", "264", "265", "266", "267", "268", "269", "27", "290", "291", "297", "298", "299",
  // Zonas 3 y 4 (Europa)
  "30", "31", "32", "33", "34", "350", "351", "352", "353", "354", "355", "356", "357", "358", "359", "36", "370",
  "371", "372", "373", "374", "375", "376", "377", "378", "380", "381", "382", "383", "385", "386", "387", "389",
  "39", "40", "41", "420", "421", "423", "43", "44", "45", "46", "47", "48", "49",
  // Zona 5 (América Latina y Caribe)
  "500", "501", "502", "503", "504", "505", "506", "507", "508", "509", "51", "52", "53", "54", "55", "56", "57",
  "58", "590", "591", "592", "593", "594", "595", "596", "597", "598", "599",
  // Zona 6 (Sudeste asiático y Oceanía)
  "60", "61", "62", "63", "64", "65", "66", "670", "672", "673", "674", "675", "676", "677", "678", "679", "680",
  "681", "682", "683", "685", "686", "687", "688", "689", "690", "691", "692",
  // Zona 8 (Asia oriental)
  "81", "82", "84", "850", "852", "853", "855", "856", "86", "880", "886",
  // Zona 9 (Asia occidental y central)
  "90", "91", "92", "93", "94", "95", "960", "961", "962", "963", "964", "965", "966", "967", "968", "970", "971",
  "972", "973", "974", "975", "976", "977", "98", "992", "993", "994", "995", "996", "998",
];

const CONJUNTO_CODIGOS = new Set(CODIGOS_PAIS_CONOCIDOS);

/** Un país del selector del formulario. */
export interface PaisSelector {
  /** Código sin «+», p. ej. "56". */
  codigo: string;
  nombre: string;
}

/** Países que ofrece el selector, en este orden (el primero es el valor por defecto). El resto va por «Otro código…». */
export const PAISES_SELECTOR: readonly PaisSelector[] = [
  { codigo: "56", nombre: "Chile" },
  { codigo: "54", nombre: "Argentina" },
  { codigo: "51", nombre: "Perú" },
  { codigo: "591", nombre: "Bolivia" },
  { codigo: "55", nombre: "Brasil" },
  { codigo: "57", nombre: "Colombia" },
  { codigo: "593", nombre: "Ecuador" },
  { codigo: "598", nombre: "Uruguay" },
  { codigo: "595", nombre: "Paraguay" },
  { codigo: "58", nombre: "Venezuela" },
  { codigo: "52", nombre: "México" },
  { codigo: "1", nombre: "Estados Unidos y Canadá" },
  { codigo: "34", nombre: "España" },
  { codigo: "33", nombre: "Francia" },
  { codigo: "49", nombre: "Alemania" },
  { codigo: "39", nombre: "Italia" },
  { codigo: "44", nombre: "Reino Unido" },
  { codigo: "351", nombre: "Portugal" },
];

/** País que viene elegido en el selector. */
export const PAIS_POR_DEFECTO = "56";

/** Valor del selector para «Otro código…» (el código se escribe a mano). */
export const OPCION_OTRO = "otro";

/** Largo total (código + número) permitido. */
export const MIN_DIGITOS_TELEFONO = 8;
export const MAX_DIGITOS_TELEFONO = 15;

/**
 * Máximo de dígitos que puede tener un número NACIONAL (sin el código de país) de cada país. Sirve SOLO para detectar un código
 * repetido (regla 7 de arriba): si el número nacional empieza con el código del país y es más largo que esto, la persona ya
 * había escrito el código. Son los largos reales más altos de cada país (sin el cero troncal, que solo se quita en Chile); en
 * duda se eligió el valor MÁS ALTO, porque rechazar un número verdadero es peor que dejar pasar un repetido raro:
 * Brasil 11 (DDD + 9 dígitos: el DDD 55 existe, así que «55 9 1234 5678» es un número válido), Argentina 11 (el 9 de los
 * móviles), Alemania 11, Italia 11, Chile 9 (8 o 9: móvil 9 + 8 dígitos, fijo con código de área).
 * El servidor copia esta tabla desde `telefonoE164.casos.json` (clave `largoNacionalMaximo`).
 */
export const LARGO_NACIONAL_MAXIMO: Readonly<Record<string, number>> = {
  "56": 9, // Chile
  "54": 11, // Argentina
  "51": 9, // Perú
  "591": 8, // Bolivia
  "55": 11, // Brasil
  "57": 10, // Colombia
  "593": 9, // Ecuador
  "598": 8, // Uruguay
  "595": 9, // Paraguay
  "58": 10, // Venezuela
  "52": 10, // México
  "1": 10, // Estados Unidos y Canadá (y el Caribe del plan +1: +1 242 Bahamas, etc.)
  "34": 9, // España
  "33": 9, // Francia
  "49": 11, // Alemania
  "39": 11, // Italia
  "44": 10, // Reino Unido
  "351": 9, // Portugal
  "7": 10, // Rusia y Kazajistán
};

/**
 * Códigos de país cuyo número NACIONAL nunca empieza con el propio código (regla 8): ahí un nacional que empieza con el código es
 * un código repetido SIN importar el largo. Hoy solo Chile (+56): no existe ningún prefijo nacional 56. En los demás países un
 * nacional puede empezar legítimamente con el código (Brasil +55 tiene el DDD 55; Kazajistán +7 tiene móviles 7xx), así que ahí
 * sigue valiendo la regla del largo máximo. El servidor copia esta lista desde
 * `telefonoE164.casos.json` (clave `codigosCuyoNacionalNuncaEmpiezaConElCodigo`).
 */
export const CODIGOS_CUYO_NACIONAL_NO_EMPIEZA_CON_SU_CODIGO: readonly string[] = ["56"];

/** Máximo nacional plausible de un país que NO está en la tabla: alto a propósito (más vale no rechazar un número verdadero). */
export const LARGO_NACIONAL_MAXIMO_GENERAL = 11;

/** Máximo de dígitos de un número nacional del país `codigo` (el de la tabla o el general). */
export const largoNacionalMaximo = (codigo: string): number => LARGO_NACIONAL_MAXIMO[codigo] ?? LARGO_NACIONAL_MAXIMO_GENERAL;

/**
 * Un «+0…» nunca es un código de país conocido. Un formulario lo antepone a un código escrito a mano que NO existe ("99", "3",
 * "568") para que el validador diga «código desconocido» en vez de reinterpretar «+99…» como el +992.
 */
export const PREFIJO_CODIGO_INEXISTENTE = "+0";

/** ¿Es un código de país conocido? (solo dígitos, sin «+»). */
export const esCodigoPaisConocido = (codigo: string): boolean => CONJUNTO_CODIGOS.has(codigo);

/** Nombre del país del selector para un código, o `null` si no está en el selector. */
export const nombreDePais = (codigo: string): string | null =>
  PAISES_SELECTOR.find((p) => p.codigo === codigo)?.nombre ?? null;

export type MotivoTelefonoInvalido =
  | "TIPO_INVALIDO"
  | "CARACTERES_NO_PERMITIDOS"
  | "SIN_DIGITOS"
  | "SIN_CODIGO_PAIS"
  | "CODIGO_PAIS_DESCONOCIDO"
  | "CERO_INICIAL_REPETIDO"
  | "POSIBLE_CODIGO_REPETIDO"
  | "MUY_CORTO"
  | "MUY_LARGO";

/** Todos los motivos, para que las pruebas (y el equipo del servidor) comprueben que no hay uno fuera de la lista. */
export const MOTIVOS_TELEFONO_INVALIDO: readonly MotivoTelefonoInvalido[] = [
  "TIPO_INVALIDO",
  "CARACTERES_NO_PERMITIDOS",
  "SIN_DIGITOS",
  "SIN_CODIGO_PAIS",
  "CODIGO_PAIS_DESCONOCIDO",
  "CERO_INICIAL_REPETIDO",
  "POSIBLE_CODIGO_REPETIDO",
  "MUY_CORTO",
  "MUY_LARGO",
];

const MENSAJES: Record<MotivoTelefonoInvalido, string> = {
  TIPO_INVALIDO: "El teléfono debe escribirse como texto, con el código de país.",
  CARACTERES_NO_PERMITIDOS:
    "El teléfono solo puede tener números. Quita las letras y los símbolos (se permiten espacios, guiones y paréntesis).",
  SIN_DIGITOS: "Escribe el número de teléfono completo, con su código de país.",
  SIN_CODIGO_PAIS: "Falta el código de país. Escribe el número con su código, por ejemplo +56 9 1234 5678.",
  CODIGO_PAIS_DESCONOCIDO: "No reconocemos ese código de país. Elige el país en la lista o revisa el código.",
  CERO_INICIAL_REPETIDO:
    "Después del código +56 el número no puede empezar con 0. Escríbelo sin el 0 inicial, por ejemplo +56 9 1234 5678.",
  POSIBLE_CODIGO_REPETIDO:
    "Parece que el número ya incluye el código de país. Quita el código del comienzo o escribe el número completo empezando con «+».",
  MUY_CORTO: `El teléfono es muy corto. Con el código de país debe tener entre ${MIN_DIGITOS_TELEFONO} y ${MAX_DIGITOS_TELEFONO} dígitos.`,
  MUY_LARGO: `El teléfono es muy largo. Con el código de país debe tener entre ${MIN_DIGITOS_TELEFONO} y ${MAX_DIGITOS_TELEFONO} dígitos.`,
};

/**
 * El texto, en español simple, que ve la persona para un motivo de rechazo. Para POSIBLE_CODIGO_REPETIDO acepta el código
 * repetido y lo nombra («Parece que ya incluye el código 56. Quita el 56 …»).
 */
export const mensajeDeMotivoTelefono = (motivo: MotivoTelefonoInvalido, codigo?: string): string =>
  motivo === "POSIBLE_CODIGO_REPETIDO" && codigo
    ? `Parece que el número ya incluye el código ${codigo}. Quita el ${codigo} del comienzo o escribe el número completo empezando con +${codigo}.`
    : MENSAJES[motivo];

export type ResultadoTelefono =
  | { ok: true; vacio: true; canonico: "" }
  | { ok: true; vacio: false; canonico: string; codigoPais: string; nacional: string }
  /** `codigoPais` solo viene en POSIBLE_CODIGO_REPETIDO: el código que parece repetido. */
  | { ok: false; motivo: MotivoTelefonoInvalido; mensaje: string; codigoPais?: string };

const rechazo = (motivo: MotivoTelefonoInvalido, codigo?: string): ResultadoTelefono => ({
  ok: false,
  motivo,
  mensaje: mensajeDeMotivoTelefono(motivo, codigo),
  ...(codigo ? { codigoPais: codigo } : {}),
});

/** Caracteres invisibles de formato (categoría Unicode Cf). Se quitan: ver la regla 3. */
const INVISIBLES = /\p{Cf}/gu;
/** Separadores: cualquier espacio, guion, guiones largos (U+2010 a U+2015), signo menos (U+2212), puntos y paréntesis. */
const SEPARADORES = /[\s\-.()\u2010-\u2015\u2212]/g;

/** NFKC y sin caracteres invisibles de formato: el texto tal como se ve. */
const comoSeVe = (texto: string): string => texto.normalize("NFKC").replace(INVISIBLES, "");

/** Lo escrito sin invisibles ni separadores: solo queda lo que cuenta (dígitos, un «+» y lo que sea inválido). */
export const limpiarTelefonoEscrito = (texto: string): string => comoSeVe(texto).replace(SEPARADORES, "");
/** Lo único que puede quedar tras quitar los separadores. */
const SOLO_DIGITOS_CON_MAS_OPCIONAL = /^\+?[0-9]+$/;

/** El código conocido más largo con el que empieza `digitos`, o `null`. */
const codigoMasLargo = (digitos: string): string | null => {
  for (let largo = Math.min(3, digitos.length); largo >= 1; largo--) {
    const candidato = digitos.slice(0, largo);
    if (CONJUNTO_CODIGOS.has(candidato)) return candidato;
  }
  return null;
};

/**
 * Compone el teléfono canónico a partir de lo que escribió la persona.
 *
 * @param entrada lo escrito (o lo recibido); `null`/`undefined` cuentan como vacío.
 * @param pais código del país elegido en el selector, sin «+» (p. ej. "56"). `null`/omitido = SIN selector (así valida el
 *   servidor): el número tiene que traer su propio código con «+» o «00».
 */
export function componerTelefono(entrada: unknown, pais?: string | null): ResultadoTelefono {
  if (entrada === null || entrada === undefined) return { ok: true, vacio: true, canonico: "" };
  if (typeof entrada !== "string") return rechazo("TIPO_INVALIDO");

  const visible = comoSeVe(entrada);
  if (visible.trim() === "") return { ok: true, vacio: true, canonico: "" };

  const limpio = visible.replace(SEPARADORES, "");
  if (limpio === "" || limpio === "+" || limpio === "00") return rechazo("SIN_DIGITOS");
  if (!SOLO_DIGITOS_CON_MAS_OPCIONAL.test(limpio)) return rechazo("CARACTERES_NO_PERMITIDOS");

  let codigo: string;
  let nacional: string;
  const trae = limpio.startsWith("+") ? 1 : limpio.startsWith("00") ? 2 : 0;
  if (trae > 0) {
    const resto = limpio.slice(trae);
    const encontrado = codigoMasLargo(resto);
    if (encontrado === null) return rechazo("CODIGO_PAIS_DESCONOCIDO");
    codigo = encontrado;
    nacional = resto.slice(encontrado.length);
  } else {
    const elegido = pais === null || pais === undefined ? "" : String(pais).replace(/^\+/, "");
    if (elegido === "") return rechazo("SIN_CODIGO_PAIS");
    if (!CONJUNTO_CODIGOS.has(elegido)) return rechazo("CODIGO_PAIS_DESCONOCIDO");
    codigo = elegido;
    nacional = limpio;
  }

  // El 0 inicial del número nacional (09…) sobra solo en Chile. Se quita UNO; si aún queda otro el número no es chileno, y
  // así el canónico nunca empieza con 0 tras el +56: validarlo otra vez lo deja igual (idempotente).
  if (codigo === "56" && nacional.startsWith("0")) {
    nacional = nacional.slice(1);
    if (nacional.startsWith("0")) return rechazo("CERO_INICIAL_REPETIDO");
  }

  const total = codigo.length + nacional.length;
  if (total < MIN_DIGITOS_TELEFONO) return rechazo("MUY_CORTO");
  if (total > MAX_DIGITOS_TELEFONO) return rechazo("MUY_LARGO");
  // Reglas 7 y 8: el número nacional ya empieza con el código y (a) es más largo que cualquier número de ese país, o (b) el país es
  // uno cuyo nacional NUNCA empieza con su código (Chile): ahí basta con que empiece, tenga el largo que tenga.
  if (nacional.startsWith(codigo) && (CODIGOS_CUYO_NACIONAL_NO_EMPIEZA_CON_SU_CODIGO.includes(codigo) || nacional.length > largoNacionalMaximo(codigo))) {
    return rechazo("POSIBLE_CODIGO_REPETIDO", codigo);
  }
  return { ok: true, vacio: false, canonico: `+${codigo}${nacional}`, codigoPais: codigo, nacional };
}

/**
 * ¿Lo escrito trae su propio código de país (empieza con «+» o «00», después de quitar los separadores)? Los formularios lo
 * usan para saber cuándo el selector de país NO manda (la persona pegó un número completo).
 */
export function traeCodigoDePais(entrada: unknown): boolean {
  if (typeof entrada !== "string") return false;
  const limpio = limpiarTelefonoEscrito(entrada);
  return limpio.startsWith("+") || limpio.startsWith("00");
}

/** Igual que `componerTelefono`, con el nombre que usan los validadores. */
export const validarTelefono = componerTelefono;

/** ¿Ya está en la forma canónica (`+` y 8 a 15 dígitos con un código conocido)? */
export const esTelefonoCanonico = (valor: unknown): boolean => {
  if (typeof valor !== "string" || !valor.startsWith("+")) return false;
  const r = componerTelefono(valor, null);
  return r.ok && !r.vacio && r.canonico === valor;
};

/** Lo que se sugiere (NUNCA se aplica solo) para un número guardado sin código de país. Solo lo inequívoco de Chile. */
const sugerenciaParaChile = (digitos: string): string | null => {
  if (/^9[0-9]{8}$/.test(digitos)) return `+56${digitos}`;
  if (/^569[0-9]{8}$/.test(digitos)) return `+${digitos}`;
  return null;
};

export type TelefonoGuardado =
  | { estado: "vacio" }
  | { estado: "completo"; canonico: string; codigoPais: string; nacional: string }
  /** Dato antiguo sin código de país. `sugerencia` es solo una propuesta que la persona debe confirmar. */
  | { estado: "sin_codigo"; digitos: string; sugerencia: string | null }
  | { estado: "invalido"; texto: string };

/**
 * Interpreta un teléfono que llega del servidor (puede ser un dato antiguo guardado sin código de país). Nunca adivina el país:
 * lo único que ofrece es una `sugerencia` de +56 cuando el número es inequívocamente chileno (9 dígitos que empiezan con 9, o
 * «56» + 9 dígitos), para que la persona la confirme.
 */
export function interpretarTelefonoGuardado(valor: unknown): TelefonoGuardado {
  const r = componerTelefono(valor, null);
  if (r.ok) {
    return r.vacio
      ? { estado: "vacio" }
      : { estado: "completo", canonico: r.canonico, codigoPais: r.codigoPais, nacional: r.nacional };
  }
  if (r.motivo === "SIN_CODIGO_PAIS") {
    // Solo se llega acá con `[0-9]+` ya validado (después de NFKC y de quitar los separadores).
    const digitos = limpiarTelefonoEscrito(String(valor));
    return { estado: "sin_codigo", digitos, sugerencia: sugerenciaParaChile(digitos) };
  }
  return { estado: "invalido", texto: typeof valor === "string" ? valor : String(valor) };
}

/**
 * Lo que se manda al servidor al GUARDAR un teléfono ya validado: el canónico, o `null` si quedó vacío. Si el valor no se puede
 * componer (un dato antiguo que la persona no tocó) se devuelve tal cual: NUNCA se borra un teléfono por no entenderlo.
 */
export function telefonoParaGuardar(valor: unknown): string | null {
  const r = componerTelefono(valor, null);
  if (r.ok) return r.vacio ? null : r.canonico;
  return typeof valor === "string" && valor.trim() !== "" ? valor.trim() : null;
}

/**
 * Regla de un `Form.Item` de Ant Design para un campo de teléfono.
 *
 * - `obligatorio`: el vacío es un error (los formularios que ya exigían el teléfono).
 * - `valorInicial`: el teléfono tal como llegó del servidor. Si la persona NO lo tocó se acepta aunque sea un dato antiguo sin
 *   código (no se le bloquea guardar otra cosa); apenas lo cambia tiene que estar completo.
 */
export function reglaTelefono(opciones: { obligatorio?: boolean; valorInicial?: string | null; mensajeObligatorio?: string } = {}) {
  return {
    validator: async (_regla: unknown, valor: unknown): Promise<void> => {
      // «Vacío» lo decide la misma regla que compone el teléfono (null, solo blancos, solo caracteres invisibles): un campo con
      // un carácter invisible pegado se ve vacío y no puede contar como lleno.
      const r = componerTelefono(valor, null);
      if (r.ok && r.vacio) {
        if (opciones.obligatorio) {
          throw new Error(opciones.mensajeObligatorio ?? "Ingresa un teléfono con su código de país.");
        }
        return;
      }
      if (typeof valor === "string" && opciones.valorInicial && valor === opciones.valorInicial) return;
      if (!r.ok) throw new Error(r.mensaje);
    },
  };
}

/**
 * Qué va al servidor por un campo de teléfono al GUARDAR, igual en todos los formularios (ronda F2):
 *   · `undefined` — no cambió respecto de `original`: el campo se OMITE del cuerpo (no se pisa lo que otra persona haya corregido);
 *   · `null`      — la persona lo vació (o se crea sin teléfono): `null` EXPLÍCITO, nunca una cadena vacía;
 *   · el canónico (+56912345678) si hay un número completo. Un dato antiguo que la persona no tocó nunca llega acá como cambio.
 * Sin `original` (una alta) no hay nada con qué comparar: se manda el canónico o `null`.
 */
export function telefonoParaEnviar(nuevo: unknown, original?: unknown): string | null | undefined {
  const aGuardar = telefonoParaGuardar(nuevo);
  if (original !== undefined && comoTextoComparable(original) === (aGuardar ?? "")) return undefined;
  return aGuardar;
}
